import { X } from "lucide-react";
import React, { useEffect } from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { LoadingInterceptor } from "../../shared/api";
import { useToast } from "../../shared/hooks/use-toast";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import { getAuthToken } from "@shared/auth/authUtils";
import { Toaster } from "sonner";
import { sidenav as Sidenav } from "@features/core/components/sidenav";
import { Footer, Topnav } from "@features/core/components";
import { Sidenav as V2Sidenav, Topnav as V2Topnav } from "../../features-v2/core/components";
import { NotificationProvider } from "../../features-v2/notification/NotificationContext";

interface MainLayoutProps {
  error?: Error | null;
  onErrorDismiss?: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  error = null,
  onErrorDismiss,
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  const location = useLocation();
  const { renderBreadcrumbs } = useBaseComponent();
  const { toast } = useToast();

  // Check for public routes
  const isPublicRoute = Boolean(
    [
      "/login",
      "/forgot-password",
      "/reset-password",
      "/",
      "/signup",
      "/setup-password",
      "/create-account"
    ].includes(location.pathname) || 
    location.pathname.match(/^\/(login|forgot-password|create-account|signup|setup-password|reset-password|guest)(\/.*)?$/)
  );

  const isAuthenticated = Boolean(getAuthToken());

  useEffect(() => {
    if (!isPublicRoute && !isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login first",
        variant: "destructive",
      });
    }
  }, [location.pathname, isPublicRoute, isAuthenticated, toast]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
        action: (
          <button
            onClick={onErrorDismiss}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 p-0 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        ),
      });
    }
  }, [error, toast, onErrorDismiss]);

  // Redirect to login if user is not authenticated and trying to access a protected route
  if (!isPublicRoute && !isAuthenticated) {
    const storedTenant = localStorage.getItem('tenant');
    const storedUtility = localStorage.getItem('utilityName');
    const loginRedirectPath = storedTenant && storedUtility 
      ? `/login/${storedTenant}/${storedUtility}` 
      : '/login';

    return <Navigate to={loginRedirectPath} replace />;
  }

  // Don't render sidebar/topnav if we're on a public route
  if (isPublicRoute) {
    return <Outlet />;
  }

  return (
    <NotificationProvider>
      <div className="relative min-h-screen ">
        <div className="flex flex-col min-h-screen">
          <V2Topnav />
          <div className={`flex min-h-screen pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'pl-[80px]' : 'pl-[260px]'}`}>
            <V2Sidenav isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
            <main className="flex-1 flex flex-col min-w-0 pb-16 relative">
              <Toaster position="top-center" richColors />
              <div className="w-full overflow-x-hidden relative">
                <Outlet />
                <LoadingInterceptor />
              </div>
            </main>
          </div>
          {/* <div className={`transition-all duration-300 sticky bottom-0 z-40 ${isSidebarCollapsed ? 'pl-[80px]' : 'pl-[260px]'}`}>
            <Footer />
          </div> */}
        </div>
      </div>
    </NotificationProvider>
  );
};

export default MainLayout;